import { DEFAULT_CHAT_SECURITY_CONFIG } from '@kanbu/schema/contracts';
import { AgentType } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import type { TypedFormState } from '@utima/ui-informed';

import { Card } from '@/components/card/Card';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';

export function SecurityFields() {
  const { t } = useLingui();

  const { schema } = useFieldsSchema(
    [
      {
        name: 'agentConfig.moderationMode',
        label: t`Content moderation`,
        tooltip: t`Standard checks each user message alongside the reply and only withholds the answer if it is flagged — no added latency for clean messages. Strict blocks the assistant until the check passes and runs an extra profanity classifier. Off disables moderation entirely.`,
        type: 'select',
        options: {
          parallel: t`Standard (recommended)`,
          blocking: t`Strict (block before answering)`,
          off: t`Off`,
        },
        defaultValue: 'parallel',
      },
      {
        // Message limits and CAPTCHA guard the web widget against abusive
        // browser traffic; neither concept applies to a phone call.
        $relevant: {
          $when: ({ formState }) =>
            (formState as TypedFormState<{ agentType?: AgentType }>).values
              ?.agentType !== AgentType.Voice,
          $fields: [
            {
              name: 'chatbotConfig.maxUserMessagesEnabled',
              label: t`Maximum user messages limit`,
              tooltip: t`Disallow sending messages after a user reaches the configured message limit (resets weekly)`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: DEFAULT_CHAT_SECURITY_CONFIG.maxUserMessagesEnabled,
              showOptional: false,
            },
            {
              $relevant: {
                $when: ({ formState }) =>
                  !!(
                    formState as TypedFormState<{
                      chatbotConfig?: {
                        maxUserMessagesEnabled?: boolean;
                      };
                    }>
                  ).values?.chatbotConfig?.maxUserMessagesEnabled,
                $fields: [
                  {
                    name: 'chatbotConfig.maxUserMessagesLimit',
                    label: t`Maximum messages per user`,
                    tooltip: t`Weekly messages limit per user. Leave empty for unlimited.`,
                    type: 'number',
                    defaultValue:
                      DEFAULT_CHAT_SECURITY_CONFIG.maxUserMessagesLimit,
                    parser: value => {
                      if (
                        value === '' ||
                        value === null ||
                        value === undefined
                      ) {
                        return null;
                      }

                      const parsedValue = Number(value);

                      return Number.isFinite(parsedValue) ? parsedValue : null;
                    },
                    mask: value => {
                      if (value === null || value === undefined) {
                        return '';
                      }

                      return String(value);
                    },
                    uiProps: {
                      min: 1,
                      step: 1,
                    },
                  },
                ],
              },
            },
            {
              name: 'chatbotConfig.captchaEnabled',
              label: t`CAPTCHA verification`,
              tooltip: t`Invisible reCAPTCHA score verification after message limit is reached`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: DEFAULT_CHAT_SECURITY_CONFIG.captchaEnabled,
              showOptional: false,
            },
            {
              $relevant: {
                $when: ({ formState }) =>
                  !!(
                    formState as TypedFormState<{
                      chatbotConfig?: {
                        captchaEnabled?: boolean;
                      };
                    }>
                  ).values?.chatbotConfig?.captchaEnabled,
                $fields: [
                  {
                    name: 'chatbotConfig.captchaLimit',
                    label: t`Message limit before CAPTCHA`,
                    tooltip: t`Number of messages a user can send before a CAPTCHA challenge is required`,
                    type: 'number',
                    defaultValue: DEFAULT_CHAT_SECURITY_CONFIG.captchaLimit,
                  },
                  {
                    name: 'chatbotConfig.captchaWindowSeconds',
                    label: t`CAPTCHA window (seconds)`,
                    tooltip: t`Time window in seconds for the message counter. Counter resets after this period.`,
                    type: 'number',
                    defaultValue:
                      DEFAULT_CHAT_SECURITY_CONFIG.captchaWindowSeconds,
                  },
                  {
                    name: 'chatbotConfig.captchaScoreThreshold',
                    label: t`Score threshold`,
                    tooltip: t`Minimum reCAPTCHA score (0.0–1.0) required to pass verification. Lower values are more permissive. Default: 0.5`,
                    type: 'number',
                    defaultValue: 0.5,
                    uiProps: {
                      min: 0,
                      max: 1,
                      step: 0.1,
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
    [t],
  );

  return (
    <Card title={t`Security and limits`} collapsed>
      <SchemaFields schema={schema} />
    </Card>
  );
}
